import { useTranslation } from 'react-i18next';
import { SEOMeta } from '../components/SEOMeta';
import { Section, Container, SectionTitle, Card } from "../components/ui";
import { RelatedPages } from '../components/RelatedPages';
import styles from "./References.module.css";

const clients = [
  { name: "Source Mobile by Bouygues Telecom", srcs: ["/images/logos/source-bouygues.png"], wide: true },
  { name: "TeleCoop", srcs: ["/images/logos/telecoop.png"] },
  { name: "Ethi'Kdo", srcs: ["/images/logos/ethikdo.png"] },
  { name: "Combak", srcs: ["/images/logos/combak.png"] },
];

const partners = [
  { name: "Shopify", srcs: ["/images/logos/shopify.webp"] },
  { name: "PrestaShop", srcs: ["/images/logos/prestashop.webp"] },
  { name: "WooCommerce", srcs: ["/images/logos/woocommerce.webp"] },
  { name: "Colissimo", srcs: ["/images/logos/colissimo.svg"] },
  { name: "Chronopost", srcs: ["/images/logos/chronopost.png"] },
  { name: "DPD Group", srcs: ["/images/logos/dpd-group.png"] },
  { name: "Sellermania", srcs: ["/images/logos/sellermania.png"] },
  { name: "Alma", srcs: ["/images/logos/alma.webp"] },
  { name: "Mangopay", srcs: ["/images/logos/mangopay.webp"] },
];

const pressmentions = [
  { media: "Europe 1", logo: "/images/logos/press/europe1.avif", quote: `QBP.Solutions « Commercialise des smartphones reconditionnés en France et en circuit court »` },
  { media: "Le Figaro", logo: "/images/logos/press/lefigaro.avif", quote: `« Le secteur du reconditionné en pleine crise de croissance »` },
  { media: "France Info", logo: "/images/logos/press/franceinfo.avif", quote: `« L'éco du lundi 21 novembre 2022, interview d'Eric Cordon »` },
  { media: "L'ADN", logo: "/images/logos/press/ladn.avif", quote: `QBP.Solutions « Lance une marketplace responsable de smartphones reconditionnés »` },
  { media: "emarketing.fr", logo: "/images/logos/press/emarketing.avif", quote: `QBP.Solutions « Dans le sillage de BackMarket, surfe sur le reconditionné made in France »` },
  { media: "Le Journal des Entreprises", logo: "/images/logos/press/journal-des-entreprises.avif", quote: `QBP.Solutions « Lance la première place de marché responsable de smartphones reconditionnés »` },
  { media: "Ekopo", logo: "/images/logos/press/ekopo.webp", quote: `« La filière du reconditionné devrait être une affaire nationale »` },
  { media: "L'ADN Data", logo: "/images/logos/press/adndata.avif", quote: `« Nous avons mis en place un système d'audit qui assure la qualité des produits. Sur les autres marketplaces il n'y a pas de qualification, ni de sélection. »` },
  { media: "Les Numériques", logo: "/images/logos/press/lesnumeriques.avif", quote: `« Pour les reconditionneurs français, c'est une aubaine pour mettre en avant le savoir-faire de leurs ateliers et valoriser des processus industriels qu'ils présentent comme exigeants. »` },
  { media: "France Mobiles", logo: "/images/logos/press/francemobiles.avif", quote: `« Le marché du smartphone reconditionné progresse mais a besoin d'être soutenu »` },
];

export function References() {
  const { t } = useTranslation('references');

  const testimonials = t('testimonials.items', { returnObjects: true }) as {
    quote: string; author: string; company: string;
  }[];

  return (
    <>
      <SEOMeta title={t('meta.title')} description={t('meta.description')} />

      <Section variant="dark" className={styles.hero}>
        <Container>
          <div className={styles.heroContent}>
            <h1>{t('hero.title')}</h1>
            <p className={styles.heroSubtitle}>{t('hero.subtitle')}</p>
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <SectionTitle title={t('clients.sectionTitle')} centered />
          <div className={styles.clientsGrid}>
            {clients.map((logo) => (
              <div key={logo.name} className={styles.logoCard}>
                {logo.srcs.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={logo.name}
                    className={logo.wide ? styles.logoImageWide : styles.logoImage}
                  />
                ))}
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <SectionTitle title={t('testimonials.sectionTitle')} centered />
          <div className={styles.testimonialsGrid}>
            {testimonials.map((testimonial, i) => (
              <Card key={i} className={styles.testimonialCard}>
                <blockquote className={styles.quote}>
                  <p>{testimonial.quote}</p>
                </blockquote>
                <footer className={styles.testimonialFooter}>
                  <span className={styles.author}>{testimonial.author}</span>
                  <span className={styles.company}>{testimonial.company}</span>
                </footer>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <SectionTitle title={t('partners.sectionTitle')} centered />
          <p className={styles.partnersSubtitle}>{t('partners.subtitle')}</p>
          <div className={styles.partnersGrid}>
            {partners.map((logo) => (
              <div key={logo.name} className={styles.logoCard}>
                {logo.srcs.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={logo.name}
                    className={styles.logoImage}
                    width={140}
                    height={60}
                    loading="lazy"
                  />
                ))}
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section variant="gray">
        <Container>
          <SectionTitle title={t('press.sectionTitle')} centered />
          <div className={styles.pressGrid}>
            {pressmentions.map((mention) => (
              <div key={mention.media} className={styles.pressCard}>
                <img src={mention.logo} alt={mention.media} className={styles.pressLogo} width={140} height={40} loading="lazy" />
                <p className={styles.pressQuote}>{mention.quote}</p>
                <span className={styles.pressMedia}>{mention.media}</span>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <RelatedPages currentFrPath="/references" />
    </>
  );
}
